export const PROBE_TABLE_CELLS = 18

const parseValue = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export const isProbeTask = (task) =>
  task?.is_probe === true || task?.is_probe === 1 || task?.is_probe === '1'

export const isProbeTableTask = (task) =>
  task?.type === 'exact-answer' && task?.probe_answer_format === 'table'

export const getProbeTableConfig = (task) => {
  const config = parseValue(task?.probe_answer_table) ?? {}
  const left = Number(config.left_cells)
  const rows = Number(config.row_count)
  return {
    left_cells: Number.isInteger(left) && left > 0 && left < PROBE_TABLE_CELLS ? left : 11,
    row_count: Number.isSafeInteger(rows) && rows > 0 ? rows : 1,
    grading: config.grading === 'ege26' ? 'ege26' : 'exact',
  }
}

// Это части одного ответа, а не альтернативные правильные ответы exact-answer.
export const normalizeProbeTableAnswer = (value) => {
  const rows = parseValue(value)
  if (!Array.isArray(rows)) return []
  return rows.map((row) =>
    [0, 1].map((column) => {
      const cell = Array.isArray(row) ? row[column] : ''
      return typeof cell === 'string' || typeof cell === 'number' ? String(cell) : ''
    }),
  )
}

export const probeTableCellsToAnswer = (cells = []) =>
  (Array.isArray(cells) ? cells : []).map((row) =>
    [0, 1].map((column) =>
      Array.isArray(row?.[column])
        ? row[column].map((cell) => (typeof cell === 'string' ? cell : '')).join('')
        : '',
    ),
  )

export const createProbeTableCells = (answer, config) => {
  const rows = normalizeProbeTableAnswer(answer)
  const widths = [config.left_cells, PROBE_TABLE_CELLS - config.left_cells]
  // Некорректный ответ сервера тоже не обрезаем при отображении.
  return Array.from({ length: Math.max(config.row_count, rows.length) }, (_, row) =>
    widths.map((width, column) => {
      const chars = Array.from(rows[row]?.[column] ?? '')
      return Array.from({ length: Math.max(width, chars.length) }, (_, index) => chars[index] ?? '')
    }),
  )
}

export const normalizeStoredProbeTableCells = (cells, config) => {
  const result = createProbeTableCells(probeTableCellsToAnswer(cells), config)
  return result.map((row, rowIndex) =>
    row.map((column, columnIndex) => {
      const stored = cells?.[rowIndex]?.[columnIndex]
      return Array.from({ length: Math.max(column.length, stored?.length ?? 0) }, (_, index) =>
        typeof stored?.[index] === 'string' ? stored[index] : '',
      )
    }),
  )
}

export const getProbeTableWidthError = (answer, leftCells) => {
  const widths = [leftCells, PROBE_TABLE_CELLS - leftCells]
  const rows = normalizeProbeTableAnswer(answer)
  for (let row = 0; row < rows.length; row++) {
    for (let column = 0; column < 2; column++) {
      const length = Array.from(rows[row][column]).length
      if (length > widths[column]) {
        return `В строке ${row + 1} столбца ${column + 1} введено ${length} символов. Допустимо ${
          widths[column]
        }. Сначала измените ответ.`
      }
    }
  }
  return ''
}

export const validateProbeTableTask = (task) => {
  if (!isProbeTableTask(task)) return ''
  const config = getProbeTableConfig(task)
  const rows = normalizeProbeTableAnswer(task.probe_table_correct_answer)
  if (!rows.length || rows.length !== config.row_count)
    return 'Заполните таблицу правильных ответов.'
  if (config.grading === 'ege26' && rows.length !== 1)
    return 'Для оценивания как в № 26 нужна одна строка.'
  const widthError = getProbeTableWidthError(rows, config.left_cells)
  if (widthError) return widthError
  for (let row = 0; row < rows.length; row++) {
    for (let column = 0; column < 2; column++) {
      const value = rows[row][column].trim()
      if (!value) return `Заполните строку ${row + 1}, столбец ${column + 1}.`
      if (/\s/.test(value))
        return `Уберите пробелы внутри значения: строка ${row + 1}, столбец ${column + 1}.`
      if (config.grading === 'ege26' && !/^-?\d+$/.test(value))
        return 'Для оценивания как в № 26 укажите два целых числа.'
    }
  }
  return ''
}

export const isProbeTablePartial = (task) =>
  isProbeTableTask(task) && getProbeTableConfig(task).grading === 'ege26'

// Старый бэкенд может молча проигнорировать новые поля. Не закрываем редактор,
// если в ответе API нет сохранённых настроек или эталона.
export const hasSavedProbeTable = (expected, saved) => {
  if (expected.type !== 'exact-answer' || !expected.probe_answer_format) return true
  if (!isProbeTask(saved)) return false
  if (saved?.probe_answer_format !== expected.probe_answer_format) return false
  if (!expected.probe_answer_table) return true
  return (
    JSON.stringify(getProbeTableConfig(expected)) === JSON.stringify(getProbeTableConfig(saved)) &&
    JSON.stringify(normalizeProbeTableAnswer(expected.probe_table_correct_answer)) ===
      JSON.stringify(normalizeProbeTableAnswer(saved?.probe_table_correct_answer))
  )
}
